import { useNavigate } from "@tanstack/solid-router";
import { createInfiniteQuery, createQuery, keepPreviousData } from "@tanstack/solid-query";
import { For, Show, createMemo, createSignal } from "solid-js";
import { SkillChips } from "~/components/SkillChips";
import { TicketClaimGate } from "~/components/TicketClaimGate";
import {
  everClaimedTicket,
  fetchOpportunities,
  fetchProfile,
  type Opportunity,
  type OpportunityType,
} from "~/lib/api";
import { profileQueryKey } from "~/lib/routes";

const TYPE_FILTERS: { id: OpportunityType | ""; label: string }[] = [
  { id: "", label: "All" },
  { id: "job", label: "Jobs" },
  { id: "gig", label: "Gigs" },
  { id: "mentorship", label: "Mentorship" },
  { id: "collab", label: "Collab" },
  { id: "speaking", label: "Speaking" },
];

function typeLabel(type: OpportunityType): string {
  return TYPE_FILTERS.find((t) => t.id === type)?.label ?? type;
}

export function OpportunitiesPage() {
  const navigate = useNavigate();
  const [type, setType] = createSignal<OpportunityType | "">("");
  const [remoteOnly, setRemoteOnly] = createSignal(false);

  const profileQuery = createQuery(() => ({
    queryKey: profileQueryKey(),
    queryFn: () => fetchProfile(),
  }));

  const needsTicket = () => profileQuery.isSuccess && !everClaimedTicket(profileQuery.data);

  const filters = createMemo(() => ({
    type: type() || undefined,
    remote_only: remoteOnly() || undefined,
  }));

  const listQuery = createInfiniteQuery(() => ({
    queryKey: ["opportunities", filters()],
    queryFn: ({ pageParam }) =>
      fetchOpportunities({
        ...filters(),
        cursor: pageParam as string | undefined,
        limit: 20,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor || undefined,
    placeholderData: keepPreviousData,
    enabled: everClaimedTicket(profileQuery.data),
  }));

  const items = createMemo(() => (listQuery.data?.pages ?? []).flatMap((p) => p.data));

  function openDetail(item: Opportunity) {
    navigate({ to: "/opportunities/$opportunityId", params: { opportunityId: item.id } });
  }

  return (
    <section class="page page--flush">
      <Show when={needsTicket()}>
        <div style={{ padding: "20px" }}>
          <TicketClaimGate cityLabel="Opportunities" />
        </div>
      </Show>

      <Show when={!needsTicket()}>
        <div class="page-title-block" style={{ padding: "20px 20px 0" }}>
          <p class="status-chip">Board</p>
          <h1>Opportunities</h1>
          <p class="muted">Roles, gigs, mentorship, and speaking — posted by the DEB community.</p>
        </div>

        <div class="filter-chips" style={{ "padding-top": "16px" }}>
          <For each={TYPE_FILTERS}>
            {(chip) => (
              <button
                type="button"
                class={`interest-chip${type() === chip.id ? " selected" : ""}`}
                onClick={() => setType(chip.id)}
              >
                {chip.label}
              </button>
            )}
          </For>
          <button
            type="button"
            class={`interest-chip${remoteOnly() ? " selected" : ""}`}
            onClick={() => setRemoteOnly((v) => !v)}
          >
            Remote
          </button>
        </div>

        <Show when={listQuery.isPending && !listQuery.data}>
          <p class="muted" style={{ padding: "0 20px" }}>
            Loading opportunities…
          </p>
        </Show>
        <Show when={listQuery.isError}>
          <div class="error-box" style={{ margin: "0 20px" }}>
            {(listQuery.error as Error).message}
          </div>
        </Show>

        <Show when={!listQuery.isPending && items().length === 0}>
          <div class="empty-state" style={{ margin: "16px 20px" }}>
            <p>No open opportunities yet.</p>
            <p class="muted">Post a role, gig, or mentorship offer for the community.</p>
          </div>
        </Show>

        <ul class="directory-grid">
          <For each={items()}>
            {(item) => (
              <li>
                <button type="button" class="directory-card" onClick={() => openDetail(item)}>
                  <div class="directory-card-body">
                    <p class="status-chip">{typeLabel(item.type)}</p>
                    <strong>{item.title}</strong>
                    <span class="muted">
                      {item.sponsor_org ?? item.location ?? (item.remote_ok ? "Remote" : "On-site")}
                    </span>
                    <Show when={item.remote_ok}>
                      <span class="open-to-badge">Remote OK</span>
                    </Show>
                    <SkillChips skills={item.skills} limit={3} />
                  </div>
                </button>
              </li>
            )}
          </For>
        </ul>

        <Show when={listQuery.hasNextPage}>
          <div class="wizard-actions" style={{ padding: "0 20px 16px" }}>
            <button
              type="button"
              class="btn btn-outline"
              disabled={listQuery.isFetchingNextPage}
              onClick={() => listQuery.fetchNextPage()}
            >
              {listQuery.isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        </Show>

        <div class="sticky-cta" style={{ padding: "0 20px 16px" }}>
          <button type="button" class="btn btn-primary" onClick={() => navigate({ to: "/opportunities/new" })}>
            {items().length ? "+ Post" : "+ Post an opportunity"}
          </button>
        </div>
      </Show>
    </section>
  );
}
